import classNames from 'classnames';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Text } from '@wix/design-system';
import { PaymentCycle } from 'enums';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { usePopoverState } from './CyclePopover';
import s from './Cycles.scss';
import { RootStore, withInjectedRootStore } from 'providers';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { getCycleKey } from 'utils';

interface CycleLabelProps extends InjectedExperimentsProps {
  cycleId: PaymentCycle;
  maxPossibleCycle: PaymentCycle;
  rootStore: RootStore;
}

const getCycleNameKey = (cycleId: string, showInMonths: boolean = false) => {
  let key = `cycle_entry.${cycleId}.name`;
  if (showInMonths) {
    switch (cycleId) {
      case PaymentCycle.YEARLY:
        key = 'cycle_entry.12_months.name';
        break;
      case PaymentCycle.YEAR2:
        key = 'cycle_entry.24_months.name';
        break;
      case PaymentCycle.YEAR3:
        key = 'cycle_entry.36_months.name';
        break;
    }
  }
  return key;
};

export const CycleLabelComp: React.FunctionComponent<CycleLabelProps> = ({
  cycleId,
  maxPossibleCycle,
  rootStore,
}) => {
  const popoverState = usePopoverState(cycleId);
  const { t } = useTranslation();
  const { theme } = usePageModeContext();
  const {
    isSecondUpgrade,
    currentProduct,
    appContext: { currentPlanDetails },
    isJapanesePackagePicker,
  } = rootStore.appStore;
  const isInCyclesOfCurrentPlan = () =>
    currentPlanDetails?.productCode === currentProduct.productCode;

  const isCurrentPlanWithMaxCycle = () =>
    maxPossibleCycle === currentPlanDetails?.paymentCycle;

  const getTooltipKey = () => {
    if (isSecondUpgrade) {
      return isCurrentPlanWithMaxCycle()
        ? 'Advanced_plans.second_upgrade.cycle.upgrade_max_cycle.disabled_tooltip'
        : isInCyclesOfCurrentPlan()
        ? 'Advanced_plans.second_upgrade.cycle.extend.disabled_tooltip'
        : 'Advanced_plans.second_upgrade.cycle.upgrade.disabled_tooltip';
    }
    return 'cycle_entry.cycle_select.disabled_tooltip';
  };
  const currentCycle = t(getCycleKey(currentPlanDetails?.paymentCycle));
  const cycleName = getCycleNameKey(cycleId, isJapanesePackagePicker);
  return (
    <div className={classNames(s.cycleEntryTitle, theme)}>
      <Popover
        showArrow
        animate
        fluid
        appendTo='window'
        placement='bottom'
        maxWidth={isSecondUpgrade ? 284 : 324}
        moveBy={{ y: 10 }}
        shown={!!popoverState}
        dataHook='cycle-name-popover'
      >
        <Popover.Element>
          <span className={s.cycleName}>{t(cycleName)}</span>
        </Popover.Element>
        <Popover.Content>
          <div className={s.cyclePopoverContent}>
            <Text dataHook='cycle-popover'>
              {t(getTooltipKey(), { currentCycle })}
            </Text>
          </div>
        </Popover.Content>
      </Popover>
    </div>
  );
};
export const CycleLabel = withInjectedRootStore(
  withExperiments(CycleLabelComp),
);
